'use client';

import { Grid3X3 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React, { useState } from 'react';

import { Activity } from '../../../../../../services/activities.service';
import { MAX_GRID_THUMBNAILS, THUMBNAIL_SIZES, getPhotoGridLayout } from './photoGridLayout';

// PhotoGalleryModal is only rendered when the user opens the gallery; lazy-load
// it so its JS (incl. createPortal + thumbnail-strip logic) stays off the
// public detail page's initial bundle.
const PhotoGalleryModal = dynamic(() => import('./PhotoGalleryModal').then((m) => m.PhotoGalleryModal), {
  ssr: false
});

interface DesktopActivityImageProps {
  activity: Activity;
}

/**
 * Desktop-specific activity image component
 * Optimized for desktop viewports with enhanced features
 */
export function DesktopActivityImage({ activity }: DesktopActivityImageProps) {
  const t = useTranslations('ActivityDetail');
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Combine main image with media URLs for complete image collection
  const allImages = [activity.imageUrl, ...(activity.mediaUrls || [])];

  const thumbnailImages = allImages.slice(1, 1 + MAX_GRID_THUMBNAILS);
  const layout = getPhotoGridLayout(thumbnailImages.length);

  const openGallery = (imageIndex: number = 0) => {
    setSelectedImageIndex(imageIndex);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  return (
    <>
      <div className={`relative grid ${layout.gridClassName} grid-rows-2 gap-3 layout-bounds h-[440px]`}>
        {/* Main image - always present */}
        <div
          className={`relative ${layout.mainClassName} rounded-lg overflow-hidden cursor-pointer group`}
          onClick={() => openGallery(0)}
        >
          <Image
            src={activity.imageUrl}
            alt={activity.title}
            fill
            sizes={layout.mainSizes}
            quality={90}
            className="object-cover transition-transform duration-200 group-hover:scale-105"
            // Lighthouse picked the desktop image as the LCP candidate even on
            // its mobile audit (Tailwind's `hidden md:block` doesn't fully hide
            // the element from LCP candidate scoring), and was flagging it for
            // `loading="lazy"`. Give it `priority` + explicit fetchPriority so
            // it's discoverable in the initial document. The preload tag this
            // emits the layout's imageSizes so
            // browsers only fetch this variant on actual desktop viewports —
            // the mobile carousel slide remains the single mobile preload.
            priority
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200" />
        </div>

        {/* Thumbnail images */}
        {thumbnailImages.map((mediaUrl, index) => (
          <div
            key={index}
            className={`relative ${layout.thumbnails[index].className} rounded-lg overflow-hidden cursor-pointer group`}
            onClick={() => openGallery(index + 1)}
          >
            <Image
              src={mediaUrl}
              alt={`${activity.title} - Image ${index + 2}`}
              fill
              sizes={THUMBNAIL_SIZES}
              className="object-cover transition-transform duration-200 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200" />
          </div>
        ))}

        {/* Show All Photos Button - Positioned relative to entire grid */}
        <button
          onClick={() => openGallery(0)}
          className="absolute bottom-4 right-4 text-neutral-500 cursor-point rounded-full bg-white px-4 py-2 shadow-lg flex items-center gap-1 z-10"
          aria-label={t('showAllPhotos')}
        >
          <Grid3X3 className="w-5 h-5" />
          <span className="text-sm font-medium">{t('showAllPhotos')}</span>
        </button>
      </div>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        images={allImages}
        isOpen={isGalleryOpen}
        onClose={closeGallery}
        initialIndex={selectedImageIndex}
        activityTitle={activity.title}
      />
    </>
  );
}
